import React from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { gql } from '@apollo/client';
import { gqlTypes } from '../../../types';
import { useHistory, Redirect } from 'react-router-dom';
import { TFunction } from 'i18next';

export const rescuePlacementPath = '/rescue/placement';

export type RescuePlacementNavigationState =
  | {
      placementView: 'petSelector';
    }
  | {
      placementView: 'petPlacement';
      petId: string;
    }
  | {
      placementView: 'petTemporaryPlacement';
      petId: string;
    }
  | {
      placementView: 'petPermanentPlacement';
      petId: string;
    }
  | {
      placementView: 'petRecall';
      petId: string;
    };

export const defaultRescuePlacementNavigationState: RescuePlacementNavigationState = {
  placementView: 'petSelector',
};

export function RedirectToRescuePlacementHome() {
  return (
    <Redirect
      to={{
        pathname: rescuePlacementPath,
        state: defaultRescuePlacementNavigationState,
      }}
    />
  );
}

export const rescuePlacementMainQuery = gql`
  query ECOMMERCE_rescuePlacementMain {
    currentUser {
      id
      transferablePets {
        ...petDetails
      }
      recallableTransferredPets {
        ...petDetails
      }
      userHouseholds {
        household {
          id
          pets {
            ...petDetails
          }
          bases {
            ...baseDetails
          }
        }
        firstCreated
      }
    }
  }

  fragment baseDetails on ChargingBase {
    id
    baseId
    name
  }

  fragment petDetails on Pet {
    id
    name
    photos {
      first {
        image {
          fullSize
        }
      }
    }
    household {
      id
      users {
        id
        firstName
        lastName
        email
      }
    }
    device {
      id
      moduleId
      currentCollarModel
    }
  }
`;

export interface RescuePlacementNavigator {
  go(state: RescuePlacementNavigationState): void;
}

export function useRescuePlacementNavigator(): RescuePlacementNavigator {
  const history = useHistory();
  return {
    go(state) {
      history.push(rescuePlacementPath, state);
    },
  };
}

export class RescuePlacementModel {
  readonly data: gqlTypes.ECOMMERCE_rescuePlacementMain;
  private readonly petsById: Map<string, gqlTypes.petDetails>;
  private readonly petIdToHouseholdId: Map<string, string>;
  private readonly unassignableBasesByHouseholdId: Map<string, gqlTypes.baseDetails[]>;

  constructor(data: gqlTypes.ECOMMERCE_rescuePlacementMain) {
    this.data = data;
    this.petsById = new Map();
    this.petIdToHouseholdId = new Map();
    this.unassignableBasesByHouseholdId = new Map();

    for (const { household } of this.data.currentUser.userHouseholds) {
      for (const pet of household.pets) {
        this.petsById.set(pet.id, pet);
        this.petIdToHouseholdId.set(pet.id, household.id);
      }
      if (household.pets.length > 1) {
        // If the household has multiple pets, rescue org may also need to unassign bases.
        for (const base of household.bases) {
          this.unassignableBasesByHouseholdId.set(
            household.id,
            (this.unassignableBasesByHouseholdId.get(household.id) ?? []).concat(base),
          );
        }
      }
    }
  }

  getPetById(id: string) {
    return this.petsById.get(id);
  }

  getUnassignableBasesForPet(petId: string) {
    const householdId = this.petIdToHouseholdId.get(petId);
    return householdId ? (this.unassignableBasesByHouseholdId.get(householdId) ?? []) : [];
  }

  associatedUserForPet(pet: gqlTypes.petDetails) {
    const associatedUsers = pet.household.users.filter((u) => u.id !== this.data.currentUser.id);
    return associatedUsers.length > 0 ? associatedUsers[0] : undefined;
  }

  get petsToBePlaced(): gqlTypes.petDetails[] {
    return this.data.currentUser.transferablePets;
  }

  get petsToBeRecalled(): gqlTypes.petDetails[] {
    const pets = this.data.currentUser.userHouseholds.map((h) => h.household.pets).flat();
    const recallableTransferredPets = this.data.currentUser.recallableTransferredPets;
    return pets.filter((pet) => recallableTransferredPets.find((p) => p.id === pet.id));
  }
}

export function transferringOwnershipTitle(pet: gqlTypes.petDetails, t: TFunction) {
  return t('transferring_pet_name_ownership', { petName: pet.name });
}

export function AccountInformation({
  data,
}: {
  data: {
    accountCreated: boolean;
    newAccountTemporaryPassword?: string | null;
  };
}) {
  const { t } = useTranslation();
  return data.accountCreated ? (
    <p>
      <Trans
        i18nKey="an_account_was_created_for_this_user_with_temporary_password"
        values={{ password: data.newAccountTemporaryPassword }}
        components={{ strong: <strong />, br: <br /> }}
      />
    </p>
  ) : (
    <p>{t('this_user_already_has_an_account')}</p>
  );
}
